import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

export interface AddressValue {
  addressLine1: string;
  addressLine2: string;
  city: string;
  postalCode: string;
  country: string;
  isManual?: boolean;
}

interface AddressSuggestion {
  id: string;
  label: string;
  street: string;
  houseNumber: string;
  city: string;
  postalCode: string;
  country: string;
}

interface AddressInputProps {
  value: AddressValue;
  onChange: (value: AddressValue) => void;
  label?: string;
}

const normalizeStreet = (input: string): string => {
  if (!input) return '';
  return input
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^0-9A-Za-zÀÂÄÇÉÈÊËÎÏÔÖÙÛÜàâäçéèêëîïôöùûü\s.'-]/g, '')
    .replace(/[ÀÂÄ]/g, 'A')
    .replace(/[àâä]/g, 'a')
    .replace(/Ç/g, 'C')
    .replace(/ç/g, 'c')
    .replace(/[ÉÈÊË]/g, 'E')
    .replace(/[éèêë]/g, 'e')
    .replace(/[ÎÏ]/g, 'I')
    .replace(/[îï]/g, 'i')
    .replace(/[ÔÖ]/g, 'O')
    .replace(/[ôö]/g, 'o')
    .replace(/[ÙÛÜ]/g, 'U')
    .replace(/[ùûü]/g, 'u');
};

const AddressInput: React.FC<AddressInputProps> = ({ value, onChange, label }) => {
  const [mode, setMode] = useState<'suggest' | 'manual'>(value.isManual ? 'manual' : 'suggest');
  const [query, setQuery] = useState(value.addressLine1 || '');
  const [options, setOptions] = useState<AddressSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestionsEnabled, setSuggestionsEnabled] = useState(true);

  useEffect(() => {
    setQuery(value.addressLine1 || '');
  }, [value.addressLine1]);

  useEffect(() => {
    if (mode !== 'suggest' || !suggestionsEnabled) {
      return;
    }

    const trimmed = query.trim();
    if (trimmed.length < 3) {
      setOptions([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const timeoutId = window.setTimeout(async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/address/suggest?q=${encodeURIComponent(trimmed)}`
        );
        if (!res.ok) {
          if (!cancelled) {
            setOptions([]);
            setError(
              "Le service de suggestion d'adresse est momentanément indisponible. Vous pouvez saisir votre adresse manuellement."
            );
            setSuggestionsEnabled(false);
            setMode('manual');
          }
          return;
        }
        const data = (await res.json()) as AddressSuggestion[];
        if (!cancelled) {
          setOptions(Array.isArray(data) ? data : []);
        }
      } catch (e) {
        if (!cancelled) {
          setOptions([]);
          setError(
            "Le service de suggestion d'adresse est momentanément indisponible. Vous pouvez saisir votre adresse manuellement."
          );
          setSuggestionsEnabled(false);
          setMode('manual');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [query, mode, suggestionsEnabled]);

  const handleSelectSuggestion = (suggestion: AddressSuggestion | null) => {
    if (!suggestion) {
      return;
    }

    const street = normalizeStreet(
      `${suggestion.street} ${suggestion.houseNumber || ''}`.trim()
    );

    onChange({
      addressLine1: street,
      addressLine2: value.addressLine2 || '',
      city: suggestion.city || value.city,
      postalCode: suggestion.postalCode || value.postalCode,
      country: suggestion.country || value.country || 'BE',
      isManual: false,
    });

    // Affiche clairement l'adresse choisie dans le champ
    setQuery(suggestion.label);
  };

  const handleManualChange = (patch: Partial<AddressValue>) => {
    const next: AddressValue = {
      addressLine1: value.addressLine1,
      addressLine2: value.addressLine2,
      city: value.city,
      postalCode: value.postalCode,
      country: value.country,
      isManual: true,
      ...patch,
    };
    onChange(next);
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {label || "Adresse de livraison"}
      </Typography>

      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
          {error}
        </Typography>
      )}

      {mode === 'suggest' && (
        <Box sx={{ mb: 2 }}>
          <Autocomplete
            fullWidth
            options={options}
            getOptionLabel={(option) => option.label}
            filterOptions={(x) => x}
            loading={loading}
            value={null}
            onChange={(_, newValue) => handleSelectSuggestion(newValue)}
            inputValue={query}
            onInputChange={(_, newInput) => setQuery(newInput)}
            clearOnBlur={false}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Adresse"
                placeholder="Commencez à taper votre adresse..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Box sx={{ mt: 1 }}>
            <Button size="small" onClick={() => setMode('manual')}>
              Saisir l'adresse manuellement
            </Button>
          </Box>
        </Box>
      )}

      {mode === 'manual' && (
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Rue et numéro (sans accents)"
            fullWidth
            value={value.addressLine1}
            onChange={(e) =>
              handleManualChange({ addressLine1: normalizeStreet(e.target.value) })
            }
          />
          <TextField
            label="Complément d'adresse"
            fullWidth
            value={value.addressLine2}
            onChange={(e) => handleManualChange({ addressLine2: e.target.value })}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Code postal"
              fullWidth
              value={value.postalCode}
              onChange={(e) => handleManualChange({ postalCode: e.target.value })}
            />
            <TextField
              label="Ville"
              fullWidth
              value={value.city}
              onChange={(e) => handleManualChange({ city: e.target.value })}
            />
          </Stack>
          <TextField
            select
            label="Pays"
            fullWidth
            value={value.country || 'BE'}
            onChange={(e) => handleManualChange({ country: e.target.value })}
            SelectProps={{ native: true }}
          >
            <option value="BE">Belgique</option>
          </TextField>
          <Box>
            <Button size="small" onClick={() => setMode('suggest')}>
              Revenir à la recherche d'adresse
            </Button>
          </Box>
        </Stack>
      )}
    </Box>
  );
};

export default AddressInput;
